/**
 * SEO Sprint 4B (Feb 2026) — FAQ Hub page.
 *
 * Combineert:
 *   - PageBanner + Breadcrumbs
 *   - Bestaande CMS-blocks via CmsPageRenderer (intro/legacy FAQ blocks)
 *   - **NIEUW**: 11 categorieën met 77 vragen via <FAQCategorySection />
 *   - **NIEUW**: "Veelgevraagde Caribbean-vragen" aggregator (5 bestemmingen)
 *   - RelatedDestinations strip
 *   - CTA-band met routes naar paklijst / online-betalen / offerte / contact
 *
 * Schema-strategie: <FAQCategorySection /> rendert één grote FAQPage
 * schema met ALLE zichtbare Q&A's uit de 11 categorieën. De legacy
 * blocks-FAQ schema-emitter blijft als secundaire bron actief; voor
 * pagina's met beide bronnen geeft Google de voorkeur aan de eerste
 * FAQPage in de DOM. Geen duplicate items want categorieën komen uit
 * een aparte CMS-collection (faq_categories_settings) en blocks uit
 * cms_pages.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Banknote, MapPin, MessageSquare } from "lucide-react";
import SEO from "../components/SEO";
import CmsPageRenderer from "../components/CmsPageRenderer";
import ExtraPageBanner from "../components/ExtraPageBanner";
import PageBreadcrumbs from "../components/PageBreadcrumbs";
import RelatedDestinations from "../components/RelatedDestinations";
import FAQCategorySection from "../components/faq/FAQCategorySection";
import CaribbeanFAQAggregator from "../components/faq/CaribbeanFAQAggregator";
import FAQSearchBar from "../components/faq/FAQSearchBar";

const SLUG = "faq";

const FAQ_BREADCRUMBS = [
  { name: "Home", url: "/" },
  { name: "Klantenservice", url: "/contact" },
  { name: "FAQ" },
];

const CTA_ITEMS = [
  {
    title: "Paklijst invullen",
    description: "Online paklijst voor douane en correcte verzending.",
    icon: FileText,
    url: "/paklijst",
    testid: "faq-cta-paklijst",
  },
  {
    title: "Online betalen",
    description: "Veilig betalen met uw W-nummer of trackingnummer.",
    icon: Banknote,
    url: "/online-betalen",
    testid: "faq-cta-online-betalen",
  },
  {
    title: "Offerte aanvragen",
    description: "Binnen 1 werkdag een prijsindicatie en routeadvies.",
    icon: MapPin,
    url: "/contact?get-quote=1#quote-form",
    testid: "faq-cta-offerte",
  },
  {
    title: "Contact opnemen",
    description: "WhatsApp, e-mail of telefoon — wij helpen u graag.",
    icon: MessageSquare,
    url: "/contact",
    testid: "faq-cta-contact",
  },
];

function FAQHubCtaSection() {
  return (
    <section
      className="bg-white border-t border-slate-100 py-14 sm:py-16"
      data-testid="faq-hub-cta"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8">
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
            Direct verder
          </span>
          <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
            Antwoord niet gevonden?
          </h2>
          <p className="mt-3 text-[#514e4e] leading-relaxed">
            Ga direct naar de juiste pagina of neem contact op — onze medewerkers helpen u graag verder.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CTA_ITEMS.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.url}
                to={c.url}
                className="group flex flex-col gap-2 bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#082c50]/40 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-18px_rgba(8,44,80,0.3)] transition-all"
                data-testid={c.testid}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#082c50] via-[#1a5193] to-[#194d8d] text-white">
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <h3 className="font-bold text-[#082c50] tracking-tight mt-1">
                  {c.title}
                </h3>
                <p className="text-sm text-[#514e4e] leading-relaxed flex-1">
                  {c.description}
                </p>
                <div className="flex items-center justify-end pt-2">
                  <ArrowRight className="w-4 h-4 text-[#1a5193] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FAQHubBody() {
  // SEO Sprint 4C — gedeelde state voor zoekbalk + category filter.
  const [searchQuery, setSearchQuery] = useState("");
  const [counts, setCounts] = useState({ total: 0, matching: 0 });
  const hasQuery = searchQuery.trim().length > 0;

  return (
    <>
      <FAQSearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        resultCount={hasQuery ? counts.matching : null}
        totalCount={counts.total}
        testid="faq-search"
      />
      <FAQCategorySection
        testid="faq-categories"
        searchQuery={searchQuery}
        onCounts={setCounts}
      />
      {/* SEO Sprint 4C — Caribbean-aggregator alleen tonen wanneer er
          geen actieve zoekterm is. Tijdens zoeken zou de aggregator
          (top-3 per destination, niet gefilterd) verwarring geven. */}
      {!hasQuery ? (
        <CaribbeanFAQAggregator testid="caribbean-faq-aggregator" />
      ) : null}
      {/* Bestaande Sprint 3 destination-links strip blijft als secundaire navigatie */}
      <RelatedDestinations
        testid="faq-related"
        eyebrow="Bestemmingen"
        title="Verzending per bestemming"
        description="Bekijk specifieke informatie en interne anchor-links per bestemming."
        anchorTemplate="Vragen over verzending naar {name}"
      />
      {/* SEO Sprint 4B — CRO-blok onderaan */}
      <FAQHubCtaSection />
    </>
  );
}

function FallbackBody() {
  return (
    <>
      <SEO
        title="Veelgestelde vragen over verzenden met 5999Cargo"
        description="Antwoorden op de meest gestelde vragen over verzenden, betalen, paklijst, douane, tracking, zee- en luchtvracht en bestemmingen naar de Caribbean."
      />
      <ExtraPageBanner slug="faq" testid="faq-banner" />
      <PageBreadcrumbs items={FAQ_BREADCRUMBS} testid="faq-breadcrumbs" />
      <FAQHubBody />
    </>
  );
}

export default function FAQPage() {
  return (
    <CmsPageRenderer
      slug={SLUG}
      testid="faq-cms-driven"
      hideBlockTypes={["hero"]}
      topSection={<ExtraPageBanner slug="faq" testid="faq-banner" />}
      breadcrumbs={<PageBreadcrumbs items={FAQ_BREADCRUMBS} testid="faq-breadcrumbs" />}
      extraSections={<FAQHubBody />}
      fallback={<FallbackBody />}
    />
  );
}
